import React, { forwardRef } from 'react-x';
import propTypes from 'prop-types-x';
import classNames from 'classnames';
import { CssAnimation } from './CssAnimation.jsx';
import { CORE_GTM_TRACKING_VALUES } from '../../constants/analytics.js';
import { fireGTMTrackingEvent } from '../../utilities/fireGTMTrackingEvent.js';

export const Button = forwardRef(function(props, ref) {

  const { 
    type, 
    label,
    gtmName,
    gtmComponentGroup, 
    gtmComponentName, 
    gtmLabel,
    gtmAction,
    disableTracking
  } = props;

  function gtmTrackingCallback() {
    if (!disableTracking) {
      fireGTMTrackingEvent({
        name: gtmName,
        componentName: gtmComponentName, 
        componentGroup: gtmComponentGroup,
        action: gtmAction,
        label: gtmLabel || label
      });
    }
  };

  const Component = type === 'tertiary' ? SimpleButton : SolidButton;

  return (
    <Component 
    {...props} 
    gtmTrackingCallback={gtmTrackingCallback} 
    ref={ref} 
    />
  )
});

const SolidButton = forwardRef(function(props, ref) {

  const { 
    type, 
    label, 
    target, 
    href, 
    overlay, 
    disabled,
    cssAnimation, 
    cssAnimationName,
    cssAnimationTrigger, 
    cssAnimationDuration,
    cssAnimationDelay,
    dummyTag,
    hoverAnimation,
    onClickCallback,
    gtmTrackingCallback,
    id,
    slim,
    icon
  } = props;

  const Tag = dummyTag ? 'div' : (href ? 'a' : 'button');

  function clickHandler(e) {
    gtmTrackingCallback();

    if (onClickCallback) {
      onClickCallback(e);
    }
  };

  return (
    <CssAnimation 
    enter={{name: cssAnimationName, target: 'element', duration: cssAnimationDuration, delay: `${cssAnimationDelay}, hidden`}} 
    triggerEnter={cssAnimationTrigger}
    disable={!cssAnimation}
    key={label}
    >
      <div 
      id={id}
      className={classNames({
        [`c-button__cta cmp-btn btn-${type}`]: true,
        [`c-button__cta--${icon} c-button__cta--custom-icon`]: !!icon,
        'c-button__cta--disabled': disabled,
        'c-button__cta--slim': slim
      })} 
      >
        <CssAnimation 
        enter={{name: 'fade-in', target: 'element', duration: 0.75, delay: `${cssAnimationDelay + 0.6}, hidden`}} 
        triggerEnter={cssAnimationTrigger}
        disable={!cssAnimation}
        >
          <Tag className={ 
            classNames({
              'btn-text': true,
              'hover-animation': hoverAnimation
            })} 
            target={target} 
            href={href} 
            data-overlay={overlay} 
            onClick={clickHandler} 
            ref={ref}
            {...((Tag === 'button' && disabled) && { disabled: true })}
            >
              {label}
          </Tag>
        </CssAnimation>
      </div>
    </CssAnimation>
  )
});

const SimpleButton = forwardRef(function(props, ref) {

  const { 
    type, 
    label, 
    target, 
    href, 
    overlay, 
    disabled,
    dummyTag,
    cssAnimation, 
    cssAnimationName,
    cssAnimationTrigger, 
    cssAnimationDuration,
    cssAnimationDelay, 
    onClickCallback, 
    gtmTrackingCallback,
    id,
    slim,
    icon
  } = props;

  const Tag = dummyTag ? 'div' : (href ? 'a' : 'button');

  function clickHandler(e) {
    gtmTrackingCallback();

    if (onClickCallback) {
      onClickCallback(e);
    }
  };

  return (
    <div 
    id={id}
    className={classNames({
      [`c-button__cta cmp-btn btn-${type}`]: true,
      [`c-button__cta--disabled`]: disabled,
      [`c-button__cta--${icon} c-button__cta--custom-icon`]: !!icon,
      'c-button__cta--slim': slim
    })}
    >
      <CssAnimation 
      enter={{name: cssAnimationName, duration: cssAnimationDuration, delay: `${cssAnimationDelay}, hidden`}} 
      triggerEnter={cssAnimationTrigger}
      disable={!cssAnimation}
      key={label}
      >
        <Tag 
        className='btn-text' 
        target={target} 
        href={href} 
        data-overlay={overlay} 
        onClick={clickHandler} 
        ref={ref}
        {...((Tag === 'button' && disabled) && { disabled: true })}
        >
          {label}
        </Tag>
      </CssAnimation>
    </div>
  )
});

Button.defaultProps = {
  type: 'primary',
  label: 'Primary CTA Text',
  href: null,
  target: '_self',
  disabled: false,
  cssAnimation: false,
  cssAnimationTrigger: 'in-view-init',
  cssAnimationDuration: 1,
  cssAnimationDelay: 0,
  hoverAnimation: true,
  onClickCallback: () => { console.log('onClick callback') },
  brand: 'lexus',
  id: null,
  gtmName: CORE_GTM_TRACKING_VALUES.name.ctaEvent,
  gtmComponentName: CORE_GTM_TRACKING_VALUES.componentName.button,
  gtmComponentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.campaigns,
  gtmAction: CORE_GTM_TRACKING_VALUES.action.clickCta,
  disableTracking: false,
  slim: false,
  icon: false
};

Button.propTypes = {
  type: propTypes.string.isRequired,
  label: propTypes.string.isRequired,
  target: propTypes.string.isRequired,
  href: propTypes.string,
  overlay: propTypes.string,
  disabled: propTypes.bool,
  cssAnimation: propTypes.bool,
  cssAnimationName: propTypes.string,
  cssAnimationTrigger: propTypes.oneOfType([
    propTypes.string,
    propTypes.bool
  ]),
  cssAnimationDuration: propTypes.number,
  cssAnimationDelay: propTypes.number,
  dummyTag: propTypes.bool,
  hoverAnimation: propTypes.bool,
  onClickCallback: propTypes.func,
  brand: propTypes.string.isRequired,
  id: propTypes.string,
  gtmName: propTypes.string,
  gtmComponentGroup: propTypes.string,
  gtmComponentName: propTypes.string,
  gtmLabel: propTypes.string,
  gtmAction: propTypes.string,
  disableTracking: propTypes.bool,
  slim: propTypes.bool,
  icon: propTypes.oneOfType([
    propTypes.string,
    propTypes.bool
  ])
};

SolidButton.defaultProps = {
  cssAnimationName: 'cta-button-width'
};

SimpleButton.defaultProps = {
  cssAnimationName: 'slide-up-fade-in'
};